/**
 * 日期时间格式化工具
 */

/**
 * 格式化日期时间为本地时间字符串
 * @param dateString ISO格式的日期时间字符串（UTC时间）
 * @param options 格式化选项
 * @returns 格式化后的本地时间字符串
 */
export const formatDateTime = (
  dateString: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!dateString) {
    return '-';
  }

  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    
    // 检查日期是否有效
    if (isNaN(date.getTime())) {
      return '-';
    }

    // 默认格式：2024-10-23 13:45:30
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    };

    return date.toLocaleString('zh-CN', options || defaultOptions);
  } catch (error) {
    console.error('日期格式化失败:', error);
    return '-';
  }
};

/**
 * 格式化日期（不含时间）
 * @param dateString ISO格式的日期字符串
 * @returns 格式化后的日期字符串，如：2024-10-23
 */
export const formatDate = (dateString: string | Date | null | undefined): string => {
  if (!dateString) {
    return '-';
  }

  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    
    if (isNaN(date.getTime())) {
      return '-';
    }

    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch (error) {
    console.error('日期格式化失败:', error);
    return '-';
  }
};

/**
 * 格式化时间（不含日期）
 * @param dateString ISO格式的日期时间字符串
 * @returns 格式化后的时间字符串，如：13:45:30
 */
export const formatTime = (dateString: string | Date | null | undefined): string => {
  if (!dateString) {
    return '-';
  }

  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    
    if (isNaN(date.getTime())) {
      return '-';
    }

    return date.toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch (error) {
    console.error('时间格式化失败:', error);
    return '-';
  }
};

/**
 * 格式化相对时间（如：3分钟前、2小时前）
 * @param dateString ISO格式的日期时间字符串
 * @returns 相对时间字符串
 */
export const formatRelativeTime = (dateString: string | Date | null | undefined): string => {
  if (!dateString) {
    return '-';
  }

  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    
    if (isNaN(date.getTime())) {
      return '-';
    }

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) {
      return '刚刚';
    } else if (diffMinutes < 60) {
      return `${diffMinutes}分钟前`;
    } else if (diffHours < 24) {
      return `${diffHours}小时前`;
    } else if (diffDays < 7) {
      return `${diffDays}天前`;
    } else {
      return formatDateTime(date);
    }
  } catch (error) {
    console.error('相对时间格式化失败:', error);
    return '-';
  }
};

/**
 * 格式化时长（秒转为时分秒）
 * @param seconds 秒数
 * @returns 格式化后的时长字符串，如：01:23:45
 */
export const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined || seconds < 0) {
    return '00:00:00';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  return [hours, minutes, secs]
    .map((v) => v.toString().padStart(2, '0'))
    .join(':');
};
